// src/pages/student-portal/courses/CoursesPage.js
// Student Portal - Browse Courses (purple theme)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    TextField,
    InputAdornment,
    Chip,
    LinearProgress,
    Skeleton,
    Pagination,
    Drawer,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    Alert,
    Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StarIcon from '@mui/icons-material/Star';
import axiosInstance from '../../../utils/axiosInstance';
import CoursePurchaseModal from './CoursePurchaseModal';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

const CATEGORY_LIST = [
    'All',
    'Safety',
    'Compliance',
    'Technical',
    'Management',
    'Soft Skills',
    'Certification',
    'Onboarding',
];

const DIFFICULTY_COLORS = {
    beginner: { bg: '#dcfce7', color: '#166534', label: 'Beginner' },
    intermediate: { bg: '#fef3c7', color: '#92400e', label: 'Intermediate' },
    advanced: { bg: '#fee2e2', color: '#991b1b', label: 'Advanced' },
};

const CARD_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
];

function getGradient(index) {
    return CARD_GRADIENTS[index % CARD_GRADIENTS.length];
}

function formatDuration(minutes) {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const CURRENCY_SYMBOLS = { usd: '$', eur: '€', gbp: '£', jpy: '¥', cad: 'CA$', aud: 'A$', inr: '₹', brl: 'R$', krw: '₩', mxn: 'MX$' };
function getCurrencySymbol(code) {
    if (!code) return '$';
    if (code.length <= 1 || CURRENCY_SYMBOLS[code.toLowerCase()]) return CURRENCY_SYMBOLS[(code || '').toLowerCase()] || '$';
    return code.toUpperCase() + ' ';
}

function getActionProps(course) {
    if (course.enrollment_status === 'completed') {
        return { label: 'Completed', variant: 'outlined', icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, disabled: true, action: 'view' };
    }
    if (course.is_enrolled) {
        return { label: 'Continue', variant: 'contained', icon: <PlayArrowIcon sx={{ fontSize: 18 }} />, disabled: false, action: 'continue' };
    }
    if (course.is_paid && !course.is_purchased) {
        return { label: 'Purchase', variant: 'contained', icon: <ShoppingCartIcon sx={{ fontSize: 18 }} />, disabled: false, action: 'purchase' };
    }
    return { label: 'Enroll', variant: 'contained', icon: <SchoolIcon sx={{ fontSize: 18 }} />, disabled: false, action: 'enroll' };
}

// ──────────────────────────────────────────────────────────
// Course Card
// ──────────────────────────────────────────────────────────
const CourseCard = ({ course, index, onEnroll, enrollingId }) => {
    const navigate = useNavigate();
    const hasThumbnail = !!course.thumbnail_url;
    const actionProps = getActionProps(course);
    const isEnrolling = enrollingId === course.course_identity;

    const handleCardClick = () => {
        navigate(`/student-portal/courses/${course.course_identity}`);
    };

    const handleAction = (e) => {
        e.stopPropagation();
        if (actionProps.action === 'enroll' || actionProps.action === 'purchase') {
            onEnroll(course.course_identity);
        } else {
            navigate(`/student-portal/courses/${course.course_identity}`);
        }
    };

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 20px 50px rgba(139, 92, 246, 0.15)',
                    borderColor: ACCENT,
                },
            }}
        >
            {/* Thumbnail */}
            <Box sx={{ position: 'relative', height: 180 }}>
                {hasThumbnail ? (
                    <Box
                        component="img"
                        src={course.thumbnail_url}
                        alt={course.title}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            background: getGradient(index),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SchoolIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.6)' }} />
                    </Box>
                )}

                {/* Status badge for enrolled courses */}
                {course.is_enrolled && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            bgcolor: course.enrollment_status === 'completed' ? '#10b981' : ACCENT,
                            color: 'white',
                            px: 1.5,
                            py: 0.4,
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            fontFamily,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        }}
                    >
                        {course.enrollment_status === 'completed' ? (
                            <><CheckCircleIcon sx={{ fontSize: 14 }} /> Completed</>
                        ) : (
                            <><PlayArrowIcon sx={{ fontSize: 14 }} /> Enrolled</>
                        )}
                    </Box>
                )}

                {/* Certificate badge */}
                {course.is_certificate_enabled && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            backdropFilter: 'blur(4px)',
                            color: '#fbbf24',
                            px: 1,
                            py: 0.4,
                            borderRadius: '8px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            fontFamily,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                        }}
                    >
                        <MilitaryTechIcon sx={{ fontSize: 14 }} /> Certificate
                    </Box>
                )}

                {/* Price badge */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        bgcolor: course.is_paid ? '#1e293b' : '#10b981',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '10px',
                        fontFamily,
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    }}
                >
                    {course.is_paid ? (
                        <>
                            {course.is_discount_active && course.discount_percentage > 0 && (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        textDecoration: 'line-through',
                                        color: 'rgba(255,255,255,0.5)',
                                        mr: 0.3,
                                    }}
                                >
                                    {getCurrencySymbol(course.currency)}{Number(course.price).toFixed(2)}
                                </Typography>
                            )}
                            {getCurrencySymbol(course.currency)}{Number(course.effective_price ?? course.price).toFixed(2)}
                            {course.is_discount_active && course.discount_percentage > 0 && (
                                <Chip
                                    label={`-${course.discount_percentage}%`}
                                    size="small"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        fontSize: '0.6rem',
                                        height: 20,
                                        bgcolor: '#ef4444',
                                        color: 'white',
                                        ml: 0.3,
                                    }}
                                />
                            )}
                        </>
                    ) : (
                        'Free'
                    )}
                </Box>
            </Box>

            {/* Progress bar for enrolled courses */}
            {course.is_enrolled && typeof course.progress === 'number' && (
                <Box sx={{ px: 0 }}>
                    <LinearProgress
                        variant="determinate"
                        value={course.progress}
                        sx={{
                            height: 4,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: course.enrollment_status === 'completed' ? '#10b981' : ACCENT,
                            },
                        }}
                    />
                </Box>
            )}

            <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
                {/* Category chip */}
                {course.category && (
                    <Chip
                        label={course.category}
                        size="small"
                        sx={{
                            fontFamily,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            bgcolor: `${ACCENT}12`,
                            color: ACCENT,
                            height: 22,
                            alignSelf: 'flex-start',
                            mb: 1,
                        }}
                    />
                )}

                {/* Title */}
                <Typography
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        color: '#1e293b',
                        fontSize: '0.95rem',
                        lineHeight: 1.4,
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {course.title}
                </Typography>

                {/* Description */}
                <Typography
                    sx={{
                        fontFamily,
                        color: '#64748b',
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flexGrow: 1,
                    }}
                >
                    {course.short_description || 'No description available.'}
                </Typography>

                {/* Meta row: difficulty, modules, duration */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    {course.difficulty_level && DIFFICULTY_COLORS[course.difficulty_level] && (
                        <Chip
                            label={DIFFICULTY_COLORS[course.difficulty_level].label}
                            size="small"
                            sx={{
                                fontFamily,
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                bgcolor: DIFFICULTY_COLORS[course.difficulty_level].bg,
                                color: DIFFICULTY_COLORS[course.difficulty_level].color,
                                height: 22,
                            }}
                        />
                    )}
                    {course.module_count != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <AutoStoriesIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>
                                {course.module_count} module{course.module_count !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}
                    {course.estimated_duration_minutes != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <AccessTimeIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>
                                {formatDuration(course.estimated_duration_minutes)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Enrolled progress text */}
                {course.is_enrolled && typeof course.progress === 'number' && (
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, mb: 1.5 }}>
                        {course.progress}% complete
                    </Typography>
                )}

                {/* Action button */}
                <Button
                    fullWidth
                    variant={actionProps.variant}
                    disabled={actionProps.disabled || isEnrolling}
                    onClick={handleAction}
                    startIcon={isEnrolling ? null : actionProps.icon}
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: '12px',
                        py: 1,
                        fontSize: '0.85rem',
                        mt: 'auto',
                        ...(actionProps.variant === 'contained'
                            ? {
                                  bgcolor: course.enrollment_status === 'completed' ? '#10b981' : ACCENT,
                                  boxShadow: 'none',
                                  '&:hover': {
                                      bgcolor: course.enrollment_status === 'completed' ? '#059669' : '#7c3aed',
                                      boxShadow: '0 4px 14px rgba(139, 92, 246, 0.4)',
                                  },
                              }
                            : {
                                  borderColor: '#10b981',
                                  color: '#10b981',
                                  '&.Mui-disabled': { borderColor: '#10b981', color: '#10b981', opacity: 0.8 },
                              }),
                    }}
                >
                    {isEnrolling ? <CircularProgress size={22} sx={{ color: 'white' }} /> : actionProps.label}
                </Button>
            </CardContent>
        </Card>
    );
};

// ──────────────────────────────────────────────────────────
// Card Skeleton
// ──────────────────────────────────────────────────────────
const CourseCardSkeleton = () => (
    <Card sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Skeleton variant="rectangular" height={180} />
        <CardContent sx={{ p: 2.5 }}>
            <Skeleton variant="rounded" width={70} height={22} sx={{ mb: 1 }} />
            <Skeleton variant="text" sx={{ fontSize: '0.95rem', mb: 0.5 }} />
            <Skeleton variant="text" sx={{ fontSize: '0.8rem', width: '80%' }} />
            <Skeleton variant="text" sx={{ fontSize: '0.8rem', width: '60%', mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Skeleton variant="rounded" width={70} height={22} />
                <Skeleton variant="rounded" width={80} height={22} />
            </Box>
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: '12px' }} />
        </CardContent>
    </Card>
);

// ──────────────────────────────────────────────────────────
// Filter Drawer
// ──────────────────────────────────────────────────────────
const FilterDrawer = ({
    open,
    onClose,
    pricing,
    setPricing,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    difficulty,
    setDifficulty,
    filterCategory,
    setFilterCategory,
    onApply,
    onClear,
}) => {
    const sectionTitle = (text) => (
        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
            {text}
        </Typography>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 380 },
                    p: 0,
                    borderRadius: { xs: 0, sm: '20px 0 0 20px' },
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 2.5,
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TuneIcon sx={{ color: ACCENT, fontSize: 22 }} />
                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>
                            Filters
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 3 }}>
                    {/* Pricing */}
                    <Box sx={{ mb: 3.5 }}>
                        {sectionTitle('Pricing')}
                        <RadioGroup
                            value={pricing}
                            onChange={(e) => setPricing(e.target.value)}
                        >
                            {[
                                { value: 'all', label: 'All' },
                                { value: 'free', label: 'Free' },
                                { value: 'paid', label: 'Paid' },
                            ].map((opt) => (
                                <FormControlLabel
                                    key={opt.value}
                                    value={opt.value}
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: '#cbd5e1',
                                                '&.Mui-checked': { color: ACCENT },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {opt.label}
                                        </Typography>
                                    }
                                    sx={{ mb: -0.5 }}
                                />
                            ))}
                        </RadioGroup>
                    </Box>

                    {/* Price Range */}
                    {pricing === 'paid' && (
                        <Box sx={{ mb: 3.5 }}>
                            {sectionTitle('Price Range')}
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField
                                    size="small"
                                    placeholder="Min"
                                    type="number"
                                    value={minPrice}
                                    onChange={(e) => setMinPrice(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>$</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            fontFamily,
                                            borderRadius: '10px',
                                            fontSize: '0.85rem',
                                        },
                                    }}
                                />
                                <TextField
                                    size="small"
                                    placeholder="Max"
                                    type="number"
                                    value={maxPrice}
                                    onChange={(e) => setMaxPrice(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>$</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            fontFamily,
                                            borderRadius: '10px',
                                            fontSize: '0.85rem',
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Level */}
                    <Box sx={{ mb: 3.5 }}>
                        {sectionTitle('Level')}
                        <RadioGroup
                            value={difficulty}
                            onChange={(e) => setDifficulty(e.target.value)}
                        >
                            {[
                                { value: 'all', label: 'All Levels' },
                                { value: 'beginner', label: 'Beginner' },
                                { value: 'intermediate', label: 'Intermediate' },
                                { value: 'advanced', label: 'Advanced' },
                            ].map((opt) => (
                                <FormControlLabel
                                    key={opt.value}
                                    value={opt.value}
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: '#cbd5e1',
                                                '&.Mui-checked': { color: ACCENT },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {opt.label}
                                        </Typography>
                                    }
                                    sx={{ mb: -0.5 }}
                                />
                            ))}
                        </RadioGroup>
                    </Box>

                    {/* Category */}
                    <Box sx={{ mb: 2 }}>
                        {sectionTitle('Category')}
                        <RadioGroup
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            {CATEGORY_LIST.map((cat) => (
                                <FormControlLabel
                                    key={cat}
                                    value={cat}
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: '#cbd5e1',
                                                '&.Mui-checked': { color: ACCENT },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {cat}
                                        </Typography>
                                    }
                                    sx={{ mb: -0.5 }}
                                />
                            ))}
                        </RadioGroup>
                    </Box>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        px: 3,
                        py: 2.5,
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        gap: 1.5,
                    }}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onClear}
                        sx={{
                            fontFamily,
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: '12px',
                            borderColor: '#e2e8f0',
                            color: '#64748b',
                            py: 1.2,
                            '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                        }}
                    >
                        Clear All
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={onApply}
                        sx={{
                            fontFamily,
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: '12px',
                            bgcolor: ACCENT,
                            boxShadow: 'none',
                            py: 1.2,
                            '&:hover': { bgcolor: '#7c3aed', boxShadow: '0 4px 14px rgba(139, 92, 246, 0.4)' },
                        }}
                    >
                        Apply Filters
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

// ──────────────────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────────────────
export default function CoursesPage() {
    const navigate = useNavigate();

    // Data state
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtering, setFiltering] = useState(false);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });

    // Search
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');

    // Category pills
    const [category, setCategory] = useState('All');

    // Filter drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pricing, setPricing] = useState('all');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [difficulty, setDifficulty] = useState('all');
    const [filterCategory, setFilterCategory] = useState('All');

    // Enroll / Purchase
    const [enrollingId, setEnrollingId] = useState(null);
    const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
    const [purchaseCourse, setPurchaseCourse] = useState(null);

    // Page
    const [page, setPage] = useState(1);

    const isInitialLoad = useRef(true);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    // Sync pill category into filter drawer category
    useEffect(() => {
        setFilterCategory(category);
    }, [category]);

    // Fetch courses
    const fetchCourses = useCallback(async () => {
        if (isInitialLoad.current) {
            setLoading(true);
            isInitialLoad.current = false;
        } else {
            setFiltering(true);
        }
        setError('');

        try {
            const params = { page, per_page: 12 };
            if (searchDebounced) params.search = searchDebounced;
            if (category && category !== 'All') params.category = category;
            if (difficulty && difficulty !== 'all') params.difficulty_level = difficulty;
            if (pricing && pricing !== 'all') params.pricing = pricing;
            if (pricing === 'paid' && minPrice) params.min_price = minPrice;
            if (pricing === 'paid' && maxPrice) params.max_price = maxPrice;

            const response = await axiosInstance.get('/api/lms/portal/courses', { params });
            const data = response.data;
            setCourses(data.data || []);
            const pg = data.pagination || {};
            setPagination({
                page: pg.page || 1,
                total: pg.total_items || pg.total || 0,
                total_pages: pg.total_pages || 1,
            });
        } catch (err) {
            console.error('Failed to fetch courses:', err);
            setError(err.response?.data?.message || 'Failed to load courses. Please try again.');
        } finally {
            setLoading(false);
            setFiltering(false);
        }
    }, [searchDebounced, category, difficulty, pricing, minPrice, maxPrice, page]);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    // Enroll handler
    const handleEnroll = useCallback(async (courseIdentity) => {
        // Check if course is paid and not yet purchased
        const targetCourse = courses.find(c => c.course_identity === courseIdentity);
        if (targetCourse?.is_paid && !targetCourse?.is_purchased) {
            setPurchaseCourse(targetCourse);
            setPurchaseModalOpen(true);
            return;
        }
        setEnrollingId(courseIdentity);
        try {
            await axiosInstance.post(`/api/lms/portal/courses/${courseIdentity}/enroll`);
            fetchCourses();
        } catch (err) {
            console.error('Enrollment failed:', err);
            setError(err.response?.data?.message || 'Enrollment failed. Please try again.');
        } finally {
            setEnrollingId(null);
        }
    }, [fetchCourses, courses]);

    const handlePurchaseSuccess = useCallback(() => {
        setPurchaseModalOpen(false);
        setPurchaseCourse(null);
        fetchCourses();
    }, [fetchCourses]);

    // Category pill click
    const handleCategoryPill = useCallback((cat) => {
        setCategory(cat);
        setFilterCategory(cat);
        setPage(1);
    }, []);

    // Filter drawer apply
    const handleApplyFilters = useCallback(() => {
        setCategory(filterCategory);
        setPage(1);
        setDrawerOpen(false);
    }, [filterCategory]);

    // Clear all filters
    const handleClearFilters = useCallback(() => {
        setSearch('');
        setSearchDebounced('');
        setCategory('All');
        setFilterCategory('All');
        setDifficulty('all');
        setPricing('all');
        setMinPrice('');
        setMaxPrice('');
        setPage(1);
    }, []);

    // Clear filters from drawer
    const handleClearDrawerFilters = useCallback(() => {
        setFilterCategory('All');
        setDifficulty('all');
        setPricing('all');
        setMinPrice('');
        setMaxPrice('');
    }, []);

    const hasActiveFilters = category !== 'All' || difficulty !== 'all' || pricing !== 'all' || searchDebounced || minPrice || maxPrice;

    const activeFilterCount = [
        category !== 'All',
        difficulty !== 'all',
        pricing !== 'all',
        minPrice || maxPrice,
    ].filter(Boolean).length;

    // Quick stats
    const totalCourseCount = pagination.total || courses.length;
    const certificateCount = courses.filter((c) => c.is_certificate_enabled).length;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', position: 'relative' }}>
            {/* Loading overlay when filtering */}
            {filtering && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(255,255,255,0.6)',
                        zIndex: 1200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(2px)',
                    }}
                >
                    <Paper
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 4,
                            py: 2.5,
                            borderRadius: '16px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        }}
                    >
                        <CircularProgress size={28} sx={{ color: ACCENT }} />
                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#475569' }}>
                            Updating results...
                        </Typography>
                    </Paper>
                </Box>
            )}

            {/* ───────── Hero Section ───────── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    px: { xs: 2, md: 4 },
                    pt: { xs: 5, md: 7 },
                    pb: { xs: 5, md: 7 },
                }}
            >
                {/* Decorative elements */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -80,
                        right: -80,
                        width: 280,
                        height: 280,
                        borderRadius: '50%',
                        bgcolor: 'rgba(139, 92, 246, 0.15)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -60,
                        left: '20%',
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        bgcolor: 'rgba(139, 92, 246, 0.08)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '40%',
                        left: -40,
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        bgcolor: 'rgba(167, 139, 250, 0.1)',
                    }}
                />

                <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    <Typography
                        sx={{
                            fontFamily,
                            fontWeight: 800,
                            color: 'white',
                            fontSize: { xs: '2rem', md: '2.8rem' },
                            mb: 1.5,
                            lineHeight: 1.2,
                        }}
                    >
                        Explore Courses
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily,
                            color: 'rgba(255,255,255,0.75)',
                            fontSize: { xs: '0.95rem', md: '1.1rem' },
                            mb: 4,
                            maxWidth: 560,
                            mx: 'auto',
                            lineHeight: 1.6,
                        }}
                    >
                        Discover courses to expand your knowledge and advance your career
                    </Typography>

                    {/* Search bar */}
                    <TextField
                        fullWidth
                        placeholder="Search courses by title, topic, or keyword..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch('')}>
                                        <CloseIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{
                            maxWidth: 640,
                            mb: 4,
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'white',
                                borderRadius: '16px',
                                fontFamily,
                                fontSize: '0.95rem',
                                py: 0.5,
                                '& fieldset': { border: 'none' },
                                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                            },
                        }}
                    />

                    {/* Quick stats */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: { xs: 3, md: 6 },
                            flexWrap: 'wrap',
                        }}
                    >
                        {[
                            { icon: <SchoolIcon sx={{ fontSize: 22 }} />, value: totalCourseCount, label: 'Total Courses' },
                            { icon: <MilitaryTechIcon sx={{ fontSize: 22 }} />, value: certificateCount, label: 'Certificates Available' },
                            { icon: <StarIcon sx={{ fontSize: 22 }} />, value: '--', label: 'Expert Instructors' },
                        ].map((stat, i) => (
                            <Box key={i} sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.8,
                                        mb: 0.3,
                                    }}
                                >
                                    <Box sx={{ color: '#a78bfa' }}>{stat.icon}</Box>
                                    <Typography
                                        sx={{
                                            fontFamily,
                                            fontWeight: 800,
                                            color: 'white',
                                            fontSize: '1.4rem',
                                        }}
                                    >
                                        {stat.value}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily,
                                        color: 'rgba(255,255,255,0.55)',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                    }}
                                >
                                    {stat.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* ───────── Main Content ───────── */}
            <Box sx={{ px: { xs: 2, md: 4 }, pb: 6, maxWidth: 1400, mx: 'auto', mt: 3 }}>
                {/* Category pills + filter button */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 3,
                        flexWrap: 'wrap',
                    }}
                >
                    {CATEGORY_LIST.map((cat) => (
                        <Chip
                            key={cat}
                            label={cat}
                            clickable
                            onClick={() => handleCategoryPill(cat)}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                px: 0.5,
                                height: 36,
                                borderRadius: '10px',
                                ...(category === cat
                                    ? {
                                          background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                          color: 'white',
                                          border: 'none',
                                          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.35)',
                                          '&:hover': { background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' },
                                      }
                                    : {
                                          bgcolor: 'white',
                                          color: '#475569',
                                          border: '1px solid #e2e8f0',
                                          '&:hover': { bgcolor: '#f1f5f9', borderColor: '#cbd5e1' },
                                      }),
                                transition: 'all 0.2s ease',
                            }}
                        />
                    ))}

                    <Button
                        onClick={() => setDrawerOpen(true)}
                        startIcon={<TuneIcon />}
                        variant="outlined"
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '10px',
                            borderColor: activeFilterCount > 0 ? ACCENT : '#e2e8f0',
                            color: activeFilterCount > 0 ? ACCENT : '#475569',
                            bgcolor: activeFilterCount > 0 ? `${ACCENT}08` : 'white',
                            height: 36,
                            px: 2,
                            ml: 0.5,
                            '&:hover': {
                                borderColor: ACCENT,
                                bgcolor: `${ACCENT}08`,
                            },
                        }}
                    >
                        Filters
                        {activeFilterCount > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    ml: 0.8,
                                    bgcolor: ACCENT,
                                    color: 'white',
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                }}
                            >
                                {activeFilterCount}
                            </Box>
                        )}
                    </Button>
                </Box>

                {/* Results header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>
                        {loading
                            ? 'Loading courses...'
                            : `Showing ${courses.length} of ${pagination.total || 0} course${(pagination.total || 0) !== 1 ? 's' : ''}`}
                    </Typography>
                    {hasActiveFilters && (
                        <Button
                            size="small"
                            onClick={handleClearFilters}
                            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                color: '#ef4444',
                                fontSize: '0.8rem',
                                '&:hover': { bgcolor: '#fef2f2' },
                            }}
                        >
                            Clear all filters
                        </Button>
                    )}
                </Box>

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchCourses} sx={{ fontFamily, textTransform: 'none' }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading skeletons */}
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 12 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} xl={3} key={i}>
                                <CourseCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : courses.length === 0 ? (
                    /* Empty state */
                    <Paper
                        sx={{
                            py: 10,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: `${ACCENT}10`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2.5,
                            }}
                        >
                            <SchoolIcon sx={{ fontSize: 40, color: ACCENT }} />
                        </Box>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.2rem', mb: 1 }}>
                            No courses found
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', mb: 3, maxWidth: 400, mx: 'auto' }}>
                            {hasActiveFilters
                                ? 'Try adjusting your filters or search terms to find what you are looking for.'
                                : 'Courses will appear here once they are published.'}
                        </Typography>
                        {hasActiveFilters && (
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderColor: ACCENT,
                                    color: ACCENT,
                                    borderRadius: '12px',
                                    px: 3,
                                    '&:hover': { borderColor: '#7c3aed', bgcolor: `${ACCENT}08` },
                                }}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Paper>
                ) : (
                    <>
                        {/* Course card grid - 4 columns on xl */}
                        <Grid container spacing={3}>
                            {courses.map((course, index) => (
                                <Grid item xs={12} sm={6} md={4} xl={3} key={course.id || course.course_identity || index}>
                                    <CourseCard
                                        course={course}
                                        index={index}
                                        onEnroll={handleEnroll}
                                        enrollingId={enrollingId}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                                <Pagination
                                    count={pagination.total_pages}
                                    page={page}
                                    onChange={(_, value) => {
                                        setPage(value);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    shape="rounded"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontFamily,
                                            fontWeight: 600,
                                            borderRadius: '10px',
                                            mx: 0.3,
                                        },
                                        '& .Mui-selected': {
                                            bgcolor: `${ACCENT} !important`,
                                            color: 'white',
                                            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.35)',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* ───────── Filter Drawer ───────── */}
            <FilterDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                pricing={pricing}
                setPricing={setPricing}
                minPrice={minPrice}
                setMinPrice={setMinPrice}
                maxPrice={maxPrice}
                setMaxPrice={setMaxPrice}
                difficulty={difficulty}
                setDifficulty={setDifficulty}
                filterCategory={filterCategory}
                setFilterCategory={setFilterCategory}
                onApply={handleApplyFilters}
                onClear={handleClearDrawerFilters}
            />

            {/* Purchase Modal */}
            <CoursePurchaseModal open={purchaseModalOpen} onClose={() => { setPurchaseModalOpen(false); setPurchaseCourse(null); }} course={purchaseCourse} onPurchaseSuccess={handlePurchaseSuccess} />
        </Box>
    );
}
